/**
 * Lime Authentication API Wrapper
 * Native JavaScript replacement for AWS Amplify's API methods
 * Adds Lime authorization headers and uses fetch API for HTTP requests
 */

import { apiEndpointsConfig } from '../../../../configuration/constants';

/**
 * Get authentication token from window.loom
 * @returns {string} - Bearer token string
 * @throws {Error} - If authentication fails
 */
function getAuthToken() {
    try {
        const authToken = `Bearer ${window.loom.getAccessToken()}`;
        return authToken;
    } catch (authError) {
        console.warn('Failed to get auth token:', authError);
        throw new Error('Unauthorized user');
    }
}

/**
 * Get API endpoint configuration by name
 * @param {string} apiName - The name of the API endpoint
 * @returns {object} - Endpoint configuration object
 * @throws {Error} - If endpoint not found
 */
function getEndpointConfig(apiName) {
    const endpointConfig = apiEndpointsConfig.find((e) => e.name === apiName);
    if (!endpointConfig) {
        throw new Error(`API endpoint '${apiName}' not found in configuration`);
    }
    return endpointConfig;
}

/**
 * Build complete URL with query parameters
 * @param {string} baseUrl - Base API URL
 * @param {string} path - API path
 * @param {object} queryStringParameters - Query parameters object
 * @returns {string} - Complete URL with query string
 */
function buildRequestUrl(baseUrl, path, queryStringParameters) {
    let url = `${baseUrl}${path}`;

    if (queryStringParameters) {
        const queryParams = new URLSearchParams();
        Object.entries(queryStringParameters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, value);
            }
        });
        const queryString = queryParams.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
    }

    return url;
}

/**
 * Prepare request headers with authorization
 * @param {string} authToken - Bearer token
 * @param {object} customHeaders - Additional headers
 * @param {boolean} includeContentType - Whether to include Content-Type header
 * @returns {object} - Headers object
 */
function prepareHeaders(authToken, customHeaders = {}, includeContentType = false) {
    const headers = {
        ...customHeaders,
    };

    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    if (authToken) {
        headers['Authorization'] = authToken;
    }

    return headers;
}

/**
 * Execute fetch request and handle response
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @returns {Promise} - Promise that resolves to parsed JSON response
 */
async function executeRequest(url, options) {
    const response = await fetch(url, options);

    // Handle non-200 responses
    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            // If response body is not valid JSON, use status text
            errorData = { errorMessage: response.statusText || 'Request failed' };
        }
        
        // Create error with backward compatibility for Amplify-style error handling
        const error = new Error(errorData.errorMessage || JSON.stringify(errorData));
        error.status = response.status;
        error.statusText = response.statusText;
        // Add response property for backward compatibility with existing error handlers
        error.response = {
            status: response.status,
            statusText: response.statusText,
            data: errorData
        };
        throw error;
    }

    // Parse JSON response
    const data = await response.json();
    return data;
}

/**
 * Native JavaScript replacement for AWS Amplify's API.get() method
 * Adds authorization header and uses fetch API for HTTP requests
 *
 * @param {string} apiName - The name of the API endpoint (e.g., 'v1/llm-service')
 * @param {string} path - The API path/operation (e.g., '/v1/chat')
 * @param {object} init - Request configuration object containing queryStringParameters, headers, etc.
 * @returns {Promise} - Promise that resolves to the API response
 */
export async function apiGet(apiName, path, init = {}) {
    try {
        const authToken = getAuthToken();
        const endpointConfig = getEndpointConfig(apiName);
        const url = buildRequestUrl(endpointConfig.endpoint, path, init.queryStringParameters);
        const headers = prepareHeaders(authToken, init.headers, false);

        const options = {
            method: 'GET',
            headers,
            ...init.options, // Allow additional fetch options to be passed through
        };

        return await executeRequest(url, options);
    } catch (error) {
        console.error(`API GET request failed [${error.status || 'Unknown'}]:`, error.message);
        throw error;
    }
}

/**
 * Retry wrapper for API calls with exponential backoff
 *
 * @param {Function} apiCall - The API call function to retry
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} baseDelay - Base delay in milliseconds (default: 1000)
 * @returns {Promise} - Promise that resolves to the API response
 */
export async function reTryCatch(apiCall, maxRetries = 3, baseDelay = 1000) {
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await apiCall();
        } catch (error) {
            lastError = error;

            // Don't retry on the last attempt
            if (attempt === maxRetries) {
                break;
            }

            // Don't retry on authentication errors or client errors (4xx)
            if (
                error.message.includes('Authentication required') ||
                error.message.includes('Unauthorized') ||
                (error.message.includes('HTTP 4') && !error.message.includes('HTTP 429'))
            ) {
                break;
            }

            // Calculate delay with exponential backoff
            const delay = baseDelay * Math.pow(2, attempt);
            console.warn(
                `API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`,
                error.message
            );

            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    throw lastError;
}

/**
 * Native JavaScript replacement for AWS Amplify's API.post() method
 * Adds authorization header and uses fetch API for HTTP POST requests
 *
 * @param {string} apiName - The name of the API endpoint (e.g., 'v1/llm-service')
 * @param {string} path - The API path/operation (e.g., '/v1/chat')
 * @param {object} init - Request configuration object containing body, headers, etc.
 * @returns {Promise} - Promise that resolves to the API response
 */
export async function apiPost(apiName, path, init = {}) {
    try {
        const authToken = getAuthToken();
        const endpointConfig = getEndpointConfig(apiName);
        const url = buildRequestUrl(endpointConfig.endpoint, path, init.queryStringParameters);
        const headers = prepareHeaders(authToken, init.headers, true);

        // Prepare request body
        let body = null;
        if (init.body) {
            body = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
        }

        const options = {
            method: 'POST',
            headers,
            body,
            ...init.options, // Allow additional fetch options to be passed through
        };

        return await executeRequest(url, options);
    } catch (error) {
        console.error(`API POST request failed [${error.status || 'Unknown'}]:`, error.message);
        throw error;
    }
}

/**
 * Native JavaScript replacement for AWS Amplify's API.put() method
 * Adds authorization header and uses fetch API for HTTP PUT requests
 *
 * @param {string} apiName - The name of the API endpoint
 * @param {string} path - The API path/operation
 * @param {object} init - Request configuration object containing body, headers, etc.
 * @returns {Promise} - Promise that resolves to the API response
 */
export async function apiPut(apiName, path, init = {}) {
    try {
        const authToken = getAuthToken();
        const endpointConfig = getEndpointConfig(apiName);
        const url = buildRequestUrl(endpointConfig.endpoint, path, init.queryStringParameters);
        const headers = prepareHeaders(authToken, init.headers, true);

        // Prepare request body
        let body = null;
        if (init.body) {
            body = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
        }

        const options = {
            method: 'PUT',
            headers,
            body,
            ...init.options,
        };

        return await executeRequest(url, options);
    } catch (error) {
        console.error(`API PUT request failed [${error.status || 'Unknown'}]:`, error.message);
        throw error;
    }
}

/**
 * Native JavaScript replacement for AWS Amplify's API.del() method
 * Adds authorization header and uses fetch API for HTTP DELETE requests
 *
 * @param {string} apiName - The name of the API endpoint
 * @param {string} path - The API path/operation
 * @param {object} init - Request configuration object containing body, queryStringParameters, headers, etc.
 * @returns {Promise} - Promise that resolves to the API response
 */
export async function apiDel(apiName, path, init = {}) {
    try {
        const authToken = getAuthToken();
        const endpointConfig = getEndpointConfig(apiName);
        const url = buildRequestUrl(endpointConfig.endpoint, path, init.queryStringParameters);
        const headers = prepareHeaders(authToken, init.headers, init.body ? true : false);

        // Prepare request body
        let body = null;
        if (init.body) {
            body = typeof init.body === 'string' ? init.body : JSON.stringify(init.body);
        }

        const options = {
            method: 'DELETE',
            headers,
            body,
            ...init.options,
        };

        return await executeRequest(url, options);
    } catch (error) {
        console.error(`API DELETE request failed [${error.status || 'Unknown'}]:`, error.message);
        throw error;
    }
}

/**
 * Legacy compatibility object that mimics AWS Amplify's API interface
 * This can be used as a drop-in replacement for existing API calls
 *
 * @example
 * import { API } from './limeAuthUtils/apiWrapper';
 * const response = await API.get('v1/llm-service', '/v1/chat', { queryStringParameters: { id: '123' } });
 */
export const API = {
    get: apiGet,
    post: apiPost,
    put: apiPut,
    del: apiDel,
};

// Export individual functions for more flexible usage
export { apiGet as get, apiPost as post, apiPut as put, apiDel as del };
